export function addClasses(...args: string[]): string {
  return args.filter((item) => !!item).join(" ");
}

/**
 * Finds if any element of targetArray is inside sourceArray
 *
 * @param {Array<string>} sourceArray - Array of strings
 * @param {Array<string>} targetArray - Array of strings
 * @param {boolean} [all=false]
 * @returns {boolean}
 */
export function listIncludes<T>(sourceArray: T[], targetArray: T[], all: boolean = false): boolean {
  if (!targetArray) {
    console.warn("list includes : empty target");
    return false;
  }
  if (all) {
    return targetArray.every((item) => sourceArray.includes(item));
  }
  return targetArray.some((item) => sourceArray.includes(item));
}

export function hasAnyItems<T>(sourceArray: T[], ...items: T[]): boolean {
  if (!items) {
    console.warn("has any items : empty target");
    return false;
  }
  return items.some((element) => sourceArray.includes(element));
}

export function oneOf<T>(item: T, list: T | T[], ...rest: T[]): boolean {
  if (!list) {
    console.warn("oneOf : empty list");
    return false;
  }
  if (Array.isArray(list)) {
    return list.includes(item);
  }
  const items = [list, ...rest];
  return items.includes(item);
}

export function hasAction(campaign: any, type: string): any {
  return campaign?.actions?.find((action: any) => action.type === type);
}

export function isAdmin(user: { profile?: string } | null | undefined): boolean {
  return user?.profile === "ADMIN";
}

export function isBooster(user: { profile?: string } | null | undefined): boolean {
  return user?.profile === "BOOSTER" && !window.premium;
}

/**
 * Parse CSV text into rows of cells, honoring RFC-4180 quoted fields:
 * quotes protect embedded separators, newlines, and escaped quotes ("").
 * A plain `text.split("\n")` breaks on headers/values that contain newlines
 * inside quotes (e.g. a "sms\nProspection" column name) — this doesn't.
 */
export function parseCSV(text: string, separator: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; } // escaped quote
        else quoted = false;
      } else cell += c;
    } else if (c === '"') {
      quoted = true;
    } else if (c === separator) {
      row.push(cell); cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++; // CRLF
      row.push(cell); cell = "";
      rows.push(row); row = [];
    } else cell += c;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => v.trim().length)); // drop blank lines
}

/**
 * Extract the values of one column from a CSV file.
 * `columnSpec` is the column name with the separator appended as its last
 * char (as produced by the column picker), e.g. "Tel client,".
 */
export function extractCSVColumn(text: string, columnSpec: string): string[] {
  const separator = columnSpec[columnSpec.length - 1];
  const name = columnSpec.slice(0, -1).trim();
  const rows = parseCSV(text, separator);
  if (!rows.length) throw new Error("Empty CSV");
  const col = rows[0].findIndex((h) => h.trim() === name);
  if (col === -1) throw new Error(`Column "${name}" not found in CSV`);
  return rows.slice(1).map((r) => (r[col] ?? "").trim()).filter(Boolean);
}

export function csvLength(file: File | null | undefined, setter: (count: number) => void): void {
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setter(result.split("\n").length - 2);
      }
    };
    reader.readAsText(file);
  } else {
    setter(0);
  }
}
